import { Button, Container, Element, Label } from '@playcanvas/pcui';
import { Mat4, path, Vec3 } from 'playcanvas';

import { DataPanel } from './data-panel';
import { Events } from '../events';
import { AboutPopup } from './about-popup';
import { BottomToolbar } from './bottom-toolbar';
import { ColorPanel } from './color-panel';
import { ExportPopup } from './export-popup';
import { ImageSettingsDialog } from './image-settings-dialog';
import { localize, localizeInit } from './localization';
import { Menu } from './menu';
import { ModeToggle } from './mode-toggle';
import logo from './playcanvas-logo.png';
import { Popup, ShowOptions } from './popup';
import { Progress } from './progress';
import { PublishSettingsDialog } from './publish-settings-dialog';
import { AnnotationModalManager } from './annotation-modal';
import { LeftAnnotationToolbar } from './left-annotation-toolbar';
import { RightToolbar } from './right-toolbar';
import { ScenePanel } from './scene-panel';
import { ShortcutsPopup } from './shortcuts-popup';
import { Spinner } from './spinner';
import { StatusBar } from './status-bar';
import { TimelinePanel } from './timeline-panel';
import { Tooltips } from './tooltips';
import { VideoSettingsDialog } from './video-settings-dialog';
import { ViewCube } from './view-cube';
import { ViewPanel } from './view-panel';
import { WorkspaceCompassOverlay } from './workspace-compass-overlay';
import { version } from '../../package.json';

// ts compiler and vscode find this type, but eslint does not
type FilePickerAcceptType = unknown;

const removeExtension = (filename: string) => {
    return filename.substring(0, filename.length - path.getExtension(filename).length);
};

class EditorUI {
    appContainer: Container;
    topContainer: Container;
    canvasContainer: Container;
    toolsContainer: Container;
    canvas: HTMLCanvasElement;
    popup: Popup;

    constructor(events: Events) {
        // Favicon: only for standalone SuperSplat. Volugraph embed loads this bundle in the same document as the SPA;
        // injecting here overrides the host favicon (persists after dev server stops / failed favicon.ico fetch).
        const w = window as unknown as {
            __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean };
            __VOLUGRAPH_SUPER_SPLAT_LOAD__?: unknown;
        };
        const volugraphHostEmbed =
            w.__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbed === true || !!w.__VOLUGRAPH_SUPER_SPLAT_LOAD__;
        if (!volugraphHostEmbed) {
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = logo;
            document.head.appendChild(link);
        }

        // app
        const appContainer = new Container({
            id: 'app-container'
        });

        // editor
        const editorContainer = new Container({
            id: 'editor-container'
        });

        // tooltips container
        const tooltipsContainer = new Container({
            id: 'tooltips-container'
        });

        // top container
        const topContainer = new Container({
            id: 'top-container'
        });

        // canvas
        const canvas = document.createElement('canvas');
        canvas.id = 'canvas';

        // app label
        const appLabel = new Label({
            id: 'app-label',
            text: `SUPERSPLAT v${version}`
        });

        // cursor label
        const cursorLabel = new Label({
            id: 'cursor-label'
        });

        let fullprecision = '';

        events.on('camera.focalPointPicked', (details: { position: Vec3 }) => {
            cursorLabel.text = `${details.position.x.toFixed(2)}, ${details.position.y.toFixed(2)}, ${details.position.z.toFixed(2)}`;
            fullprecision = `${details.position.x}, ${details.position.y}, ${details.position.z}`;
        });

        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            cursorLabel.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        cursorLabel.dom.addEventListener('pointerdown', () => {
            navigator.clipboard.writeText(fullprecision);

            const orig = cursorLabel.text;
            cursorLabel.text = localize('cursor.copied');
            setTimeout(() => {
                cursorLabel.text = orig;
            }, 1000);
        });

        // canvas container
        const canvasContainer = new Container({
            id: 'canvas-container'
        });

        // tools container
        const toolsContainer = new Container({
            id: 'tools-container'
        });

        // tooltips
        const tooltips = new Tooltips();
        tooltipsContainer.append(tooltips);

        // bottom toolbar
        const scenePanel = new ScenePanel(events, tooltips);
        const viewPanel = new ViewPanel(events, tooltips);
        const colorPanel = new ColorPanel(events, tooltips);
        const bottomToolbar = new BottomToolbar(events, tooltips);
        const rightToolbar = new RightToolbar(events, tooltips);
        const leftAnnotationToolbar = new LeftAnnotationToolbar(events, tooltips);
        const modeToggle = new ModeToggle(events, tooltips);
        const menu = new Menu(events);

        canvasContainer.dom.appendChild(canvas);
        canvasContainer.append(appLabel);
        canvasContainer.append(cursorLabel);
        canvasContainer.append(toolsContainer);
        canvasContainer.append(scenePanel);
        canvasContainer.append(viewPanel);
        canvasContainer.append(colorPanel);
        canvasContainer.append(bottomToolbar);
        canvasContainer.append(rightToolbar);
        canvasContainer.append(leftAnnotationToolbar);
        canvasContainer.append(modeToggle);
        canvasContainer.append(menu);

        // Annotation modals (mounted after toolbars so z-index overlays them)
        const _annotationModalManager = new AnnotationModalManager(events, canvasContainer.dom);
        void _annotationModalManager;

        // view axes container
        const workspaceCompass = new WorkspaceCompassOverlay(events);
        canvasContainer.append(workspaceCompass);
        const viewCube = new ViewCube(events);
        canvasContainer.append(viewCube);
        events.on('prerender', (cameraMatrix: Mat4) => {
            workspaceCompass.update(cameraMatrix);
            viewCube.update(cameraMatrix);
        });

        // main container
        const mainContainer = new Container({
            id: 'main-container'
        });

        const timelinePanel = new TimelinePanel(events, tooltips);
        const dataPanel = new DataPanel(events);
        const statusBar = new StatusBar(events, tooltips);

        timelinePanel.hidden = true;

        mainContainer.append(canvasContainer);
        mainContainer.append(timelinePanel);
        mainContainer.append(dataPanel);
        mainContainer.append(statusBar);

        // Wire up status bar panel toggles
        events.on('statusBar.panelChanged', (panel: string | null) => {
            timelinePanel.hidden = panel !== 'timeline';
            dataPanel.hidden = panel !== 'splatData';
        });

        editorContainer.append(mainContainer);

        tooltips.register(cursorLabel, localize('cursor.click-to-copy'), 'top');

        // message popup
        const popup = new Popup(tooltips);

        // shortcuts popup
        const shortcutsPopup = new ShortcutsPopup(events);

        // export popup
        const exportPopup = new ExportPopup(events);

        // publish settings
        const publishSettingsDialog = new PublishSettingsDialog(events);

        // image settings
        const imageSettingsDialog = new ImageSettingsDialog(events);

        // video settings
        const videoSettingsDialog = new VideoSettingsDialog(events);

        // about popup
        const aboutPopup = new AboutPopup();

        topContainer.append(popup);
        topContainer.append(exportPopup);
        topContainer.append(publishSettingsDialog);
        topContainer.append(imageSettingsDialog);
        topContainer.append(videoSettingsDialog);
        topContainer.append(shortcutsPopup);
        topContainer.append(aboutPopup);

        appContainer.append(editorContainer);
        appContainer.append(topContainer);
        appContainer.append(tooltipsContainer);

        this.appContainer = appContainer;
        this.topContainer = topContainer;
        this.canvasContainer = canvasContainer;
        this.toolsContainer = toolsContainer;
        this.canvas = canvas;
        this.popup = popup;

        const volugraphRoot = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_ROOT__?: HTMLElement })
            .__VOLUGRAPH_SUPER_SPLAT_ROOT__;
        const volugraphCfgEmbed =
            (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphEmbed?: boolean } }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphEmbed === true ||
            !!(window as unknown as { __VOLUGRAPH_SUPER_SPLAT_LOAD__?: unknown }).__VOLUGRAPH_SUPER_SPLAT_LOAD__;
        // Never append embed UI to document.body — causes a full-page ghost viewer + runaway scroll
        // if __VOLUGRAPH_SUPER_SPLAT_ROOT__ was cleared during Strict Mode / cleanup (main.ts waits for root).
        const mountEl = volugraphRoot ?? (volugraphCfgEmbed ? null : document.body);
        if (!mountEl) {
            // eslint-disable-next-line no-console
            console.warn('[Volugraph] EditorUI: embed mode without mount root — skipping append (should not run if main() gated correctly)');
        } else {
            mountEl.appendChild(appContainer.dom);
            mountEl.setAttribute('tabIndex', '-1');
        }
        if (volugraphRoot) {
            const volugraphConfig = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: {
                volugraphEditMode?: boolean;
                volugraphCommerceMode?: boolean;
                /** Default true: hide bottom bar (Timeline / Splat Data tabs + stats). Set false to restore OEM UI. */
                volugraphHideStatusBar?: boolean;
                /** Default true: hide top-centre Centers/Rings tab. Set false to restore. */
                volugraphHideModeToggle?: boolean;
                /** Default true: use Volugraph triangle for spinner/progress. Set false for SuperSplat ring. */
                volugraphUseBrandedLoading?: boolean;
            } }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;

            appContainer.dom.setAttribute('data-volugraph-embed', 'true');
            const hideStatusBar = volugraphConfig?.volugraphHideStatusBar !== false;
            const hideModeToggle = volugraphConfig?.volugraphHideModeToggle !== false;
            appContainer.dom.setAttribute('data-volugraph-embed-status-bar', hideStatusBar ? 'hidden' : 'visible');
            appContainer.dom.setAttribute('data-volugraph-embed-mode-toggle', hideModeToggle ? 'hidden' : 'visible');

            const isCommerceEmbed = volugraphConfig?.volugraphCommerceMode === true;
            if (isCommerceEmbed) {
                volugraphRoot.setAttribute('data-commerce-mode', 'true');
            }

            /*
             * Volugraph embed: StatusBar (timeline + splat data toggles + stats), TimelinePanel, DataPanel,
             * and top-centre ModeToggle stay constructed (SuperSplat upstream) but are hidden for branding.
             * Re-enable: set volugraphHideStatusBar / volugraphHideModeToggle to false in SuperSplatMountHost.js.
             */
            if (hideStatusBar) {
                (statusBar.dom as HTMLElement).style.setProperty('display', 'none', 'important');
                (timelinePanel.dom as HTMLElement).style.setProperty('display', 'none', 'important');
                (dataPanel.dom as HTMLElement).style.setProperty('display', 'none', 'important');
            } else {
                statusBar.hidden = false;
                (statusBar.dom as HTMLElement).style.removeProperty('display');
                (timelinePanel.dom as HTMLElement).style.removeProperty('display');
                (dataPanel.dom as HTMLElement).style.removeProperty('display');
            }
            if (hideModeToggle) {
                (modeToggle.dom as HTMLElement).style.setProperty('display', 'none', 'important');
            } else {
                modeToggle.hidden = false;
                (modeToggle.dom as HTMLElement).style.removeProperty('display');
            }

            scenePanel.hidden = true;
            viewPanel.hidden = true;
            colorPanel.hidden = true;
            appLabel.hidden = true;
            cursorLabel.hidden = true;
            menu.hidden = true;
            appContainer.dom.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;margin:0;padding:0;';
            // Show editor toolbars when data-edit-mode is true (edit mode), hide when false (view mode)
            let lastEmbedGridNeutral: boolean | null = null;
            let lastSyncedEditMode: boolean | null = null;
            const syncEditMode = (editMode?: boolean) => {
                const anim = volugraphRoot.getAttribute('data-animation-mode') === 'true';
                const isEdit = editMode !== undefined
                    ? editMode
                    : (volugraphRoot.getAttribute('data-edit-mode') === 'true' || !!volugraphConfig?.volugraphEditMode);
                if (anim) {
                    bottomToolbar.hidden = true;
                    leftAnnotationToolbar.hidden = true;
                    rightToolbar.hidden = true;
                    viewCube.hidden = true;
                    (bottomToolbar.dom as HTMLElement).style.display = 'none';
                    (bottomToolbar.dom as HTMLElement).style.zIndex = '';
                    (leftAnnotationToolbar.dom as HTMLElement).style.display = 'none';
                    (leftAnnotationToolbar.dom as HTMLElement).style.zIndex = '';
                    (rightToolbar.dom as HTMLElement).style.display = 'none';
                    (rightToolbar.dom as HTMLElement).style.zIndex = '';
                    (viewCube.dom as HTMLElement).style.display = 'none';
                    workspaceCompass.hidden = true;
                    (workspaceCompass.dom as HTMLElement).style.display = 'none';
                    /* Status bar never selects “timeline” when hidden — keep panel visible for animation/export */
                    timelinePanel.hidden = false;
                    (timelinePanel.dom as HTMLElement).style.setProperty('display', 'flex', 'important');
                    (timelinePanel.dom as HTMLElement).style.zIndex = '100';
                    timelinePanel.dom.classList.add('volugraph-animation-panel');
                    const wSceneAnim = (window as unknown as {
                        scene?: { config?: { show?: { gridNeutral?: boolean } }; forceRender?: boolean };
                    }).scene;
                    if (wSceneAnim?.config?.show) {
                        const wantNeutralAnim = true;
                        if (lastEmbedGridNeutral !== wantNeutralAnim) {
                            lastEmbedGridNeutral = wantNeutralAnim;
                            wSceneAnim.config.show.gridNeutral = wantNeutralAnim;
                            wSceneAnim.forceRender = true;
                        }
                    }
                    return;
                }
                timelinePanel.dom.classList.remove('volugraph-animation-panel');
                (timelinePanel.dom as HTMLElement).style.removeProperty('z-index');
                if (isCommerceEmbed) {
                    workspaceCompass.hidden = true;
                    (workspaceCompass.dom as HTMLElement).style.setProperty('display', 'none', 'important');
                } else {
                    workspaceCompass.hidden = false;
                    (workspaceCompass.dom as HTMLElement).style.removeProperty('display');
                }
                if (hideStatusBar) {
                    (timelinePanel.dom as HTMLElement).style.setProperty('display', 'none', 'important');
                } else {
                    (timelinePanel.dom as HTMLElement).style.removeProperty('display');
                }
                bottomToolbar.hidden = !isEdit;
                leftAnnotationToolbar.hidden = !isEdit;
                rightToolbar.hidden = true;
                viewCube.hidden = !isEdit;
                (bottomToolbar.dom as HTMLElement).style.display = isEdit ? 'flex' : 'none';
                (bottomToolbar.dom as HTMLElement).style.zIndex = isEdit ? '100' : '';
                (leftAnnotationToolbar.dom as HTMLElement).style.display = isEdit ? 'flex' : 'none';
                (leftAnnotationToolbar.dom as HTMLElement).style.zIndex = isEdit ? '90' : '';
                (rightToolbar.dom as HTMLElement).style.display = 'none';
                (rightToolbar.dom as HTMLElement).style.zIndex = '';
                (viewCube.dom as HTMLElement).style.display = isEdit ? 'block' : 'none';
                (viewCube.dom as HTMLElement).style.zIndex = isEdit ? '95' : '';
                if (lastSyncedEditMode !== isEdit) {
                    lastSyncedEditMode = isEdit;
                    events.fire('volugraph.editModeChange', isEdit);
                }
                // Ground grid: only touch gridNeutral + forceRender when the value changes. Polling every 400ms
                // with forceRender true each time made the infinite-grid shader (stochastic depth) flash axes.
                const wScene = (window as unknown as {
                    scene?: { config?: { show?: { gridNeutral?: boolean } }; forceRender?: boolean };
                }).scene;
                if (wScene?.config?.show) {
                    const wantNeutral = !isEdit;
                    if (lastEmbedGridNeutral !== wantNeutral) {
                        lastEmbedGridNeutral = wantNeutral;
                        wScene.config.show.gridNeutral = wantNeutral;
                        wScene.forceRender = true;
                    }
                }
            };
            syncEditMode();
            const editModeObserver = new MutationObserver(() => syncEditMode());
            editModeObserver.observe(volugraphRoot, { attributes: true, attributeFilter: ['data-edit-mode', 'data-animation-mode'] });
            volugraphRoot.addEventListener('volugraph-edit-mode-change', ((e: CustomEvent<{ editMode: boolean }>) => {
                syncEditMode(e.detail?.editMode ?? false);
            }) as EventListener);
            events.on('volugraph.editModeChange', (editMode: boolean) => syncEditMode(editMode));
            events.on('volugraph.lightboxOpen', (anchor: HTMLElement) => {
                volugraphRoot.dispatchEvent(
                    new CustomEvent('volugraph-lightbox-open', { bubbles: true, detail: { anchor } })
                );
            });
            const volugraphEmbedCleanup = window as unknown as { __VOLUGRAPH_EDIT_MODE_CLEANUP__?: () => void };
            const getOnSetThumbnail = () => (window as unknown as {
                __VOLUGRAPH_ON_SET_THUMBNAIL__?: () => void | Promise<void>;
            }).__VOLUGRAPH_ON_SET_THUMBNAIL__;
            let syncSetThumbnailTool: (() => void) | null = null;
            if (!isCommerceEmbed) {
                const setThumbBtn = new Button({
                    id: 'bottom-toolbar-set-thumbnail',
                    class: 'bottom-toolbar-tool'
                });
                // Regular-weight Phosphor (outline stroke)
                setThumbBtn.dom.innerHTML =
                    '<i class="ph ph-image-square volugraph-set-thumbnail-toolbar-icon" aria-hidden="true"></i>';
                let lastThumbToolAvailable: boolean | null = null;
                syncSetThumbnailTool = () => {
                    const available = typeof getOnSetThumbnail() === 'function';
                    setThumbBtn.enabled = available;
                    const tip = available ? 'Set view as thumbnail' : 'Thumbnail not available in this view';
                    if (lastThumbToolAvailable === null || lastThumbToolAvailable !== available) {
                        lastThumbToolAvailable = available;
                        tooltips.unregister(setThumbBtn);
                        tooltips.register(setThumbBtn, tip, 'top');
                    }
                };
                syncSetThumbnailTool();
                // Read window on each click so React can update the host callback without remounting SuperSplat.
                setThumbBtn.on('click', () => {
                    const fn = getOnSetThumbnail();
                    if (fn) void fn();
                });
                bottomToolbar.append(setThumbBtn);
                bottomToolbar.append(new Element({ class: 'bottom-toolbar-separator' }));
            }
            let setViewCentreBtn: Button | null = null;
            if (isCommerceEmbed) {
                setViewCentreBtn = new Button({
                    id: 'bottom-toolbar-set-view-centre',
                    class: 'bottom-toolbar-set-view-centre',
                });
                (setViewCentreBtn.dom as HTMLElement).innerHTML =
                    '<span class="volugraph-set-centre-btn-inner">' +
                    '<svg class="volugraph-set-centre-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
                    '<circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2" fill="none" />' +
                    '<line x1="12" y1="4" x2="12" y2="20" stroke="currentColor" stroke-width="2" stroke-linecap="round" />' +
                    '<line x1="4" y1="12" x2="20" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />' +
                    '</svg>' +
                    '<span class="volugraph-set-centre-label">SET CENTRE</span></span>';
                tooltips.register(
                    setViewCentreBtn,
                    'Click on the product to set the storefront orbit centre',
                    'top',
                );
                setViewCentreBtn.on('click', () => {
                    events.fire('tool.orbitAnchor');
                });
                events.on('tool.activated', (toolName: string | null) => {
                    setViewCentreBtn?.class[toolName === 'orbitAnchor' ? 'add' : 'remove']('active');
                });
                bottomToolbar.append(setViewCentreBtn);
                bottomToolbar.append(new Element({ class: 'bottom-toolbar-separator' }));
            }
            const saveExitBtn = new Button({
                id: 'bottom-toolbar-volugraph-save-exit',
                class: 'bottom-toolbar-save-exit',
                text: 'SAVE'
            });
            /** Phosphor floppy-disk (host app loads @phosphor-icons/web/bold) + SAVE */
            (saveExitBtn.dom as HTMLElement).innerHTML =
                '<span class="volugraph-save-btn-inner">' +
                '<i class="ph ph-bold ph-floppy-disk volugraph-save-icon" aria-hidden="true"></i>' +
                '<span class="volugraph-save-label">SAVE</span></span>';
            let lastSaveExitTip: string | null = null;
            const syncSaveExitLabel = () => {
                const attr = volugraphRoot.getAttribute('data-volugraph-needs-save');
                let needsSave: boolean;
                if (attr === 'false') {
                    needsSave = false;
                } else if (attr === 'true') {
                    needsSave = true;
                } else {
                    const sceneDirty = events.invoke('scene.dirty') as boolean | undefined;
                    const annotDirty = events.invoke('annotation.dirty') as boolean | undefined;
                    needsSave = (sceneDirty !== false) || !!annotDirty;
                }
                saveExitBtn.enabled = needsSave;
                const dom = saveExitBtn.dom as HTMLElement;
                dom.classList.toggle('volugraph-save-disabled', !needsSave);
                const label = 'SAVE';
                const tip = needsSave
                    ? 'Save splat, annotations, and return to view mode'
                    : 'No unsaved changes';
                const labelEl = dom.querySelector('.volugraph-save-label');
                if (labelEl) {
                    labelEl.textContent = label;
                } else {
                    saveExitBtn.text = label;
                }
                if (lastSaveExitTip !== tip) {
                    lastSaveExitTip = tip;
                    tooltips.unregister(saveExitBtn);
                    tooltips.register(saveExitBtn, tip, 'top');
                }
            };
            syncSaveExitLabel();
            const saveExitLabelAttrObserver = new MutationObserver(() => syncSaveExitLabel());
            saveExitLabelAttrObserver.observe(volugraphRoot, {
                attributes: true,
                attributeFilter: ['data-volugraph-needs-save']
            });
            const saveExitLabelPoll = setInterval(() => syncSaveExitLabel(), 400);
            const prevVolugraphCleanup = volugraphEmbedCleanup.__VOLUGRAPH_EDIT_MODE_CLEANUP__;
            volugraphEmbedCleanup.__VOLUGRAPH_EDIT_MODE_CLEANUP__ = () => {
                prevVolugraphCleanup?.();
                clearInterval(saveExitLabelPoll);
                saveExitLabelAttrObserver.disconnect();
            };
            saveExitBtn.on('click', () => {
                if (!saveExitBtn.enabled) return;
                volugraphRoot.dispatchEvent(new CustomEvent('volugraph-save-exit', { bubbles: true }));
            });
            bottomToolbar.append(saveExitBtn);
            const exitEditorBtn = new Button({
                id: 'bottom-toolbar-volugraph-exit-editor',
                // Single class only — PCUI passes `class` to classList.add (one token); pill styles use #bottom-toolbar-volugraph-exit-editor in volugraph-embed.scss
                class: 'bottom-toolbar-exit-editor',
                text: 'EXIT'
            });
            tooltips.register(
                exitEditorBtn,
                'Leave edit mode. Unsaved 3D edits will ask for confirmation.',
                'top',
            );
            exitEditorBtn.on('click', () => {
                volugraphRoot.dispatchEvent(new CustomEvent('volugraph-exit-editor', { bubbles: true }));
            });
            bottomToolbar.append(exitEditorBtn);
            const editModePoll = setInterval(() => {
                syncEditMode();
                syncSetThumbnailTool?.();
            }, 400);
            const prevAfterExitToolbar = volugraphEmbedCleanup.__VOLUGRAPH_EDIT_MODE_CLEANUP__;
            volugraphEmbedCleanup.__VOLUGRAPH_EDIT_MODE_CLEANUP__ = () => {
                prevAfterExitToolbar?.();
                clearInterval(editModePoll);
                editModeObserver.disconnect();
            };
        }
        const focusTarget = mountEl ?? (appContainer.dom as HTMLElement);

        events.on('show.shortcuts', () => {
            shortcutsPopup.hidden = false;
        });

        events.function('show.exportPopup', (exportType, splatNames: [string], showFilenameEdit: boolean) => {
            return exportPopup.show(exportType, splatNames, showFilenameEdit);
        });

        events.function('show.publishSettingsDialog', async () => {
            // show popup if user isn't logged in
            const userStatus = await events.invoke('publish.userStatus');
            if (!userStatus) {
                await events.invoke('showPopup', {
                    type: 'error',
                    header: localize('popup.error'),
                    message: localize('popup.publish.please-log-in')
                });
                return false;
            }

            // get user publish settings
            const publishSettings = await publishSettingsDialog.show(userStatus);

            // do publish
            if (publishSettings) {
                await events.invoke('scene.publish', publishSettings);
            }
        });

        events.function('show.imageSettingsDialog', async () => {
            const imageSettings = await imageSettingsDialog.show();

            if (imageSettings) {
                await events.invoke('render.image', imageSettings);
            }
        });

        events.function('show.videoSettingsDialog', async () => {
            const volugraphCfg = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphVideoExportEnabled?: boolean } })
                .__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
            if (volugraphCfg?.volugraphVideoExportEnabled === false) {
                events.fire('volugraph.videoExportBlocked', { reason: 'plan' });
                return;
            }
            const videoSettings = await videoSettingsDialog.show();

            if (videoSettings) {

                try {
                    const docName = events.invoke('doc.name');

                    // Determine file extension and mime type based on format
                    let fileExtension: string;
                    let filePickerTypes: FilePickerAcceptType[];

                    // Codec name mapping for display
                    const codecNames: Record<string, string> = {
                        'h264': 'H.264',
                        'h265': 'H.265',
                        'vp9': 'VP9',
                        'av1': 'AV1'
                    };
                    const codecName = codecNames[videoSettings.codec] || videoSettings.codec.toUpperCase();

                    if (videoSettings.format === 'webm') {
                        fileExtension = '.webm';
                        filePickerTypes = [{
                            description: `WebM Video (${codecName})`,
                            accept: { 'video/webm': ['.webm'] }
                        }];
                    } else if (videoSettings.format === 'mov') {
                        fileExtension = '.mov';
                        filePickerTypes = [{
                            description: `MOV Video (${codecName})`,
                            accept: { 'video/quicktime': ['.mov'] }
                        }];
                    } else if (videoSettings.format === 'mkv') {
                        fileExtension = '.mkv';
                        filePickerTypes = [{
                            description: `MKV Video (${codecName})`,
                            accept: { 'video/x-matroska': ['.mkv'] }
                        }];
                    } else {
                        fileExtension = '.mp4';
                        filePickerTypes = [{
                            description: `MP4 Video (${codecName})`,
                            accept: { 'video/mp4': ['.mp4'] }
                        }];
                    }

                    const vgBase = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphVideoExportBasename?: string } })
                        .__VOLUGRAPH_SUPER_SPLAT_CONFIG__?.volugraphVideoExportBasename?.trim();
                    const exportStem = vgBase
                        ? removeExtension(vgBase)
                        : (() => {
                            const stem = removeExtension(docName ?? 'volugraph-flythrough');
                            return stem === 'supersplat' ? 'volugraph-flythrough' : stem;
                        })();
                    const _suggested = `${exportStem}${fileExtension}`;
                    void _suggested;
                    void filePickerTypes;

                    // Always export via browser download so the finished video lands in Downloads automatically.
                    await events.invoke('render.video', videoSettings, undefined);
                } catch (error) {
                    if (error instanceof DOMException && error.name === 'AbortError') {
                        // user cancelled save dialog
                        return;
                    }

                    await events.invoke('showPopup', {
                        type: 'error',
                        header: 'Video export failed',
                        message: `'${error.message ?? error}'`
                    });
                }
            }
        });

        events.on('show.about', () => {
            aboutPopup.hidden = false;
        });

        events.function('showPopup', (options: ShowOptions) => {
            return this.popup.show(options);
        });

        // spinner with reference counting to handle nested operations
        const spinner = new Spinner();
        topContainer.append(spinner);

        let spinnerCount = 0;

        events.on('startSpinner', () => {
            spinnerCount++;
            if (spinnerCount === 1) {
                spinner.hidden = false;
            }
        });

        events.on('stopSpinner', () => {
            spinnerCount = Math.max(0, spinnerCount - 1);
            if (spinnerCount === 0) {
                spinner.hidden = true;
            }
        });

        // progress

        const progress = new Progress();

        topContainer.append(progress);

        const exportProgressTitle = () => localize('panel.render.exporting-video');

        const setVolugraphExporting = (active: boolean) => {
            const root = volugraphRoot
                ?? (document.querySelector('[data-volugraph-supersplat]') as HTMLElement | null);
            if (root) {
                if (active) root.setAttribute('data-volugraph-exporting', 'true');
                else root.removeAttribute('data-volugraph-exporting');
            }
            if (active) appContainer.dom.setAttribute('data-volugraph-exporting', 'true');
            else appContainer.dom.removeAttribute('data-volugraph-exporting');
            const shell = root?.closest?.('.studio-viewer-shell')
                ?? document.querySelector('.studio-viewer-shell.is-animation-mode');
            if (shell) {
                if (active) shell.setAttribute('data-volugraph-exporting', 'true');
                else shell.removeAttribute('data-volugraph-exporting');
            }
        };

        events.on('progressStart', (header: string, cancellable?: boolean) => {
            // dismissSuperSplatLoadingChrome() can leave pcui-hidden on #progress-container; host CSS
            // also sets opacity:0 after splat-ready — volugraph-export-active must override all of that.
            progress.dom.classList.remove('pcui-hidden');
            progress.dom.classList.add('volugraph-export-active');
            progress.dom.removeAttribute('hidden');
            progress.dom.removeAttribute('aria-hidden');
            progress.dom.style.removeProperty('opacity');
            progress.hidden = false;
            progress.setProgress(0);
            progress.showCancelButton(!!cancellable);
            progress.onCancel = cancellable ? () => events.fire('progressCancel') : null;

            if (cancellable) {
                progress.dom.classList.add('volugraph-video-export-progress');
                progress.setHeader('');
                progress.setText(`${exportProgressTitle()}  0%`);
                setVolugraphExporting(true);
            } else {
                progress.dom.classList.remove('volugraph-video-export-progress');
                progress.setHeader(header);
                progress.setText('');
                setVolugraphExporting(false);
            }
        });

        events.on('progressUpdate', (options: { text?: string, progress?: number }) => {
            let clampedProgress: number | undefined;
            if (options.progress !== undefined && Number.isFinite(options.progress)) {
                clampedProgress = Math.max(0, Math.min(100, options.progress));
                progress.setProgress(clampedProgress);
            }

            if (progress.dom.classList.contains('volugraph-video-export-progress')) {
                if (clampedProgress !== undefined) {
                    progress.setText(`${exportProgressTitle()}  ${Math.round(clampedProgress)}%`);
                }
                return;
            }

            if (options.text !== undefined && clampedProgress !== undefined) {
                progress.setText(`${options.text} ${Math.round(clampedProgress)}%`);
            } else if (options.text !== undefined) {
                progress.setText(options.text);
            } else if (clampedProgress !== undefined) {
                progress.setText(`${Math.round(clampedProgress)}%`);
            }
        });

        events.on('progressEnd', () => {
            progress.hidden = true;
            progress.dom.classList.remove('volugraph-export-active');
            progress.dom.classList.remove('volugraph-video-export-progress');
            progress.dom.setAttribute('aria-hidden', 'true');
            progress.showCancelButton(false);
            progress.onCancel = null;
            setVolugraphExporting(false);
        });

        if (volugraphRoot) {
            const vcfg = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphUseBrandedLoading?: boolean } }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
            const useBrandedLoading = vcfg?.volugraphUseBrandedLoading !== false;
            if (useBrandedLoading) {
                const base = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_BASE__?: string }).__VOLUGRAPH_SUPER_SPLAT_BASE__ || '';
                let markUrl = '';
                try {
                    markUrl = new URL('static/volugraph/volugraph-triangle.png', base).href;
                } catch {
                    markUrl = '';
                }
                if (markUrl) {
                    spinner.dom.classList.add('volugraph-branded-loading');
                    const spinnerImg = document.createElement('img');
                    spinnerImg.className = 'volugraph-loading-mark';
                    spinnerImg.src = markUrl;
                    spinnerImg.alt = '';
                    spinnerImg.draggable = false;
                    spinner.dom.prepend(spinnerImg);

                    progress.dom.classList.add('volugraph-branded-loading');
                    const wrap = document.createElement('div');
                    wrap.className = 'volugraph-progress-branding';
                    const pImg = document.createElement('img');
                    pImg.className = 'volugraph-loading-mark';
                    pImg.src = markUrl;
                    pImg.alt = '';
                    pImg.draggable = false;
                    wrap.appendChild(pImg);
                    progress.dom.prepend(wrap);
                }
            }
        }

        // initialize canvas to correct size before creating graphics device etc
        const wcfg = (window as unknown as { __VOLUGRAPH_SUPER_SPLAT_CONFIG__?: { volugraphMaxPixelRatio?: number } }).__VOLUGRAPH_SUPER_SPLAT_CONFIG__;
        const cap = wcfg?.volugraphMaxPixelRatio;
        const pixelRatio = (typeof cap === 'number' && cap > 0)
            ? Math.min(window.devicePixelRatio, cap)
            : window.devicePixelRatio;
        canvas.width = Math.ceil(canvasContainer.dom.offsetWidth * pixelRatio);
        canvas.height = Math.ceil(canvasContainer.dom.offsetHeight * pixelRatio);

        ['contextmenu', 'gesturestart', 'gesturechange', 'gestureend'].forEach((event) => {
            document.addEventListener(event, (e) => {
                e.preventDefault();
            }, true);
        });

        // whenever the canvas container is clicked, set keyboard focus on the body
        canvasContainer.dom.addEventListener('pointerdown', (event: PointerEvent) => {
            // set focus on the body if user is busy pressing on the canvas or a child of the tools
            // element
            if (event.target === canvas || toolsContainer.dom.contains(event.target as Node)) {
                focusTarget.focus();
            }
        }, true);
    }
}

export { EditorUI };
